import { useContext, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';

import {
  ActionOverlayPopover,
  Card,
  CopyIcon,
  EditableTitle,
  Layout,
  MinusPlusButtons,
  Pill,
  Toolbar,
  TrashIcon,
} from '@utilities';

import {
  CONCEPT_IMAGE_SURVEY_TYPES,
  SURVEY_GROUP_VALIDATION_STATUS,
  SURVEY_TYPES,
  VIDEO_MULTI_GROUP_RESPONSE_COUNT,
  VIDEO_RESPONSE_COUNT_INCREMENT,
  VIDEO_SINGLE_GROUP_RESPONSE_COUNT,
} from '@api/instant_survey';
import { responseCountIncrement, responseCountMinimum } from '@api/surveys';

import AvailablePanelists from './components/AvailablePanelists';
import GroupAdvancedPrompts from './components/GroupAdvancedPrompts';
import GroupMadlib from './components/GroupMadlib';
import {
  GROUP_NAME_REQUIRED_ERROR,
  GROUP_NAME_UNIQUE_ERROR,
} from '../../../../utilities/constants';
import ModalPromptSearch from '../../../../../../components/ModalPromptSearch';
import { SurveyContext } from '../../../../utilities/context';
import {
  hasSampleValidationCriteria,
  normalizePromptAnswer,
  transformPromptSelections,
} from '../../utilities/helpers';

import ConceptDetails from './components/ConceptDetails';

import styles from './_index.module.scss';

const clonePromptAnswer = (answer) => {
  const cloned = cloneDeep(answer);
  delete cloned.id;
  delete cloned._delete;
  return cloned;
};

const QuotaGroupCard = ({
  canDecrementResponses,
  canDelete,
  canDuplicate,
  canIncrementResponses,
  getVisibleBehaviorCount,
  group,
  isValidatingResponses,
  isVideoSurvey,
  onDelete,
  onDuplicate,
  onUpdate,
  setVisibleBehaviorCount,
  survey,
  surveyType,
  updateVisiblePrompts,
}) => {
  const { activeGroups, isReadOnly } = useContext(SurveyContext);
  const [activePrompt, setActivePrompt] = useState(null);
  const [isActionOverlayOpen, setIsActionOverlayOpen] = useState(false);

  const responseMinimum = isVideoSurvey
    ? activeGroups.length > 1
      ? VIDEO_MULTI_GROUP_RESPONSE_COUNT
      : VIDEO_SINGLE_GROUP_RESPONSE_COUNT
    : responseCountMinimum;
  const responseIncrement = isVideoSurvey ? VIDEO_RESPONSE_COUNT_INCREMENT : responseCountIncrement;

  const isContinuouslyFed = useMemo(() => {
    const activeGroup = survey.groups.find(
      ({ clientId, id }) => id === group.id || clientId === group.clientId
    );

    if (!activeGroup) return false;

    const datePrompts = activeGroup.promptAnswers?.filter((answer) => answer.fieldType === 'DATE');

    return datePrompts.some(
      (datePrompt) => datePrompt?.values.length === 1 && typeof datePrompt?.values[0] === 'string'
    );
  }, [survey, group.clientId, group.id]);

  // REHYDRATE PROMPT AND SELECTED VALUES FOR MODALPROMPTSEARCH
  const activeHierarchySearchAnswers = useMemo(() => {
    // based originally from transformSearchAnswers from the Builder helper functions
    if (!activePrompt) return {};

    const [basePromptKey, suffix] = activePrompt.promptKey.split('_');

    let matchingHierarchyKey;
    if (!suffix) {
      matchingHierarchyKey = surveyType?.hierarchy_search_prompts
        ?.find((promptCombo) => promptCombo.includes(activePrompt.insightsKey))
        ?.filter((prompt) => prompt !== activePrompt.insightsKey)?.[0];
    } else {
      matchingHierarchyKey = surveyType?.hierarchy_search_prompts
        ?.find((promptCombo) => promptCombo.includes(basePromptKey))
        ?.filter((prompt) => prompt !== basePromptKey)?.[0];
    }

    if (!matchingHierarchyKey) return {};

    const matchingHierarchyPrompt = surveyType.prompts.find(({ insightsKey, promptKey }) => {
      if (!suffix) {
        return insightsKey === matchingHierarchyKey;
      } else {
        const [promptBaseKey, promptSuffix] = promptKey?.split('_') || [];
        if (promptBaseKey === matchingHierarchyKey) {
          if (promptSuffix === suffix) return true;
        }
        return false;
      }
    });

    const matchingHieraryAnswers = group.promptAnswers
      .filter(({ _delete, promptId }) => !_delete && promptId === matchingHierarchyPrompt?.id)
      .map(({ values }) => values)
      .flat()
      .map(({ attributeId, id }) => ({ attributeId, id }));

    return matchingHieraryAnswers.length > 0
      ? {
          [matchingHierarchyKey]: matchingHieraryAnswers,
        }
      : {};
  }, [activePrompt, group, surveyType]);
  const activePromptForSearch = activePrompt
    ? {
        ...activePrompt,
        hasSearch: activePrompt.isSearchEnabled,
        id: activePrompt.insightsKey,
        max: activePrompt.maxValues,
        min: activePrompt.minAnswers,
      }
    : null;
  const activeSelectedAnswers = activePrompt
    ? group.promptAnswers
        .filter(({ _delete, promptId }) => !_delete && promptId === activePrompt.id)
        .map(({ values }) => values)
        .flat()
    : null;

  const getGroupNameError = () => {
    if (!group.name.trim()) return GROUP_NAME_REQUIRED_ERROR;
    if (
      survey.groups
        .filter(({ _delete }) => !_delete)
        .filter(({ name }) => name.trim() === group.name.trim()).length > 1
    ) {
      return GROUP_NAME_UNIQUE_ERROR;
    }
    return null;
  };

  const getGroupStatus = () => {
    switch (group.validationStatus) {
      case SURVEY_GROUP_VALIDATION_STATUS.ERROR:
      case SURVEY_GROUP_VALIDATION_STATUS.NOT_ENOUGH_SAMPLE:
        return 'warn';
      case SURVEY_GROUP_VALIDATION_STATUS.VALIDATED:
        return 'success';
      default:
        return 'info';
    }
  };

  const isGroupStatusUpdated = useMemo(() => {
    return [
      SURVEY_GROUP_VALIDATION_STATUS.ERROR,
      SURVEY_GROUP_VALIDATION_STATUS.NOT_ENOUGH_SAMPLE,
      SURVEY_GROUP_VALIDATION_STATUS.VALIDATED,
    ].includes(group.validationStatus);
  }, [group.validationStatus]);
  const hasValidationCriteria = useMemo(
    () => hasSampleValidationCriteria({ group, surveyType }),
    [group, surveyType]
  );

  const onClearAnswer = (promptId, fieldToRemove = null) => {
    const promptAnswers = cloneDeep(group.promptAnswers);
    promptAnswers.forEach((promptAnswer) => {
      const isMatch = promptAnswer.promptId === promptId;
      const isFieldMatch = fieldToRemove ? promptAnswer.field === fieldToRemove : true;

      if (isMatch && isFieldMatch) {
        promptAnswer._delete = true;
        promptAnswer.values = [];
      }
    });

    const updateData = { promptAnswers };

    // Only clear conceptName/conceptImage when the item prompt (new_item) is cleared
    if (surveyType.id === SURVEY_TYPES.NEW_ITEM) {
      const clearedPrompt = surveyType.prompts?.find((p) => p.id === promptId);

      // Only clear concept details if the item prompt is being cleared
      if (clearedPrompt?.insightsKey === 'new_item') {
        const newItemPromptIds = new Set(
          (surveyType.prompts || [])
            .filter((prompt) => prompt.insightsKey === 'new_item')
            .map((prompt) => prompt.id)
        );

        const hasActiveItemAnswers = promptAnswers.some(
          (answer) =>
            newItemPromptIds.has(answer.promptId) &&
            !answer._delete &&
            answer.values &&
            answer.values.length > 0
        );

        if (!hasActiveItemAnswers) {
          updateData.conceptName = '';
          updateData.conceptImage = null;
        }
      }
    }

    onUpdate(updateData);
  };

  const onUpdateAnswers = (newAnswers, selectedPrompt = activePrompt, isDuplicate = false) => {
    if (!selectedPrompt && isDuplicate) {
      const existingAnswers = group.promptAnswers.map((answer) => cloneDeep(answer));

      newAnswers.forEach((newAnswer) => {
        const sanitizedAnswer = normalizePromptAnswer(clonePromptAnswer(newAnswer));
        const index = existingAnswers.findIndex(
          (answer) =>
            answer.promptId === sanitizedAnswer.promptId && answer.field === sanitizedAnswer.field
        );

        if (index !== -1) {
          existingAnswers[index] = {
            ...existingAnswers[index],
            ...sanitizedAnswer,
            _delete: undefined,
          };
        } else {
          existingAnswers.push(sanitizedAnswer);
        }
      });

      onUpdate({ promptAnswers: existingAnswers });
      setActivePrompt(null);
      return;
    }

    const groupAnswers = cloneDeep(group.promptAnswers);
    const promptAnswers = transformPromptSelections({
      answers: newAnswers,
      prompt: selectedPrompt,
    });

    groupAnswers
      .filter((answer) => answer.promptId === selectedPrompt.id)
      .forEach((existingAnswer) => {
        const exists = promptAnswers.some(
          (newAnswer) =>
            newAnswer.promptId === existingAnswer.promptId &&
            newAnswer.field === existingAnswer.field
        );

        if (!exists) {
          existingAnswer._delete = true;
          existingAnswer.values = [];
        }
      });

    promptAnswers.forEach((promptAnswer) => {
      const existing = groupAnswers.find(
        (groupAnswer) =>
          groupAnswer.promptId === promptAnswer.promptId && groupAnswer.field === promptAnswer.field
      );

      if (existing) {
        delete existing._delete;
        existing.operator = promptAnswer.operator;
        existing.values = promptAnswer.values;
      } else {
        groupAnswers.push(promptAnswer);
      }
    });

    const updateData = { promptAnswers: groupAnswers };

    if (surveyType.id === SURVEY_TYPES.NEW_ITEM && selectedPrompt?.insightsKey === 'new_item') {
      const newProductName = promptAnswers.find(
        (answer) => answer.values && answer.values.length > 0
      )?.values?.[0]?.name;

      if (newProductName) {
        updateData.conceptName = newProductName;
        updateData.conceptImage = null;
      } else {
        updateData.conceptName = '';
        updateData.conceptImage = null;
      }
    }

    onUpdate(updateData);
    setActivePrompt(null);
  };

  return (
    <Card as={Layout.Flex} className={styles['quota-group-card']} status={getGroupStatus()}>
      <div className={styles['quota-group-card-panelists']}>
        <Toolbar
          tools={
            isReadOnly
              ? []
              : [
                  <ActionOverlayPopover
                    isActive={isActionOverlayOpen}
                    key="quota-group-actions"
                    onClick={() => setIsActionOverlayOpen(!isActionOverlayOpen)}
                    onHide={() => setIsActionOverlayOpen(false)}
                  >
                    <ul>
                      <li>
                        <button
                          disabled={!canDuplicate}
                          onClick={() => {
                            onDuplicate();
                            setIsActionOverlayOpen(false);
                          }}
                        >
                          <CopyIcon />
                          <span>Duplicate</span>
                        </button>
                      </li>
                      <hr />
                      <li>
                        <button
                          disabled={!canDelete}
                          onClick={() => {
                            onDelete();
                            setIsActionOverlayOpen(false);
                          }}
                        >
                          <TrashIcon />
                          <span>Delete</span>
                        </button>
                      </li>
                    </ul>
                  </ActionOverlayPopover>,
                ]
          }
        >
          <EditableTitle
            error={getGroupNameError()}
            isDisabled={isReadOnly}
            onChange={(value) => onUpdate({ name: value })}
            value={group.name}
          />
        </Toolbar>

        {isContinuouslyFed && (
          <div className={styles['quota-group-card-badges']}>
            <Pill isCompleted value="Continuously Fed Sample" />
          </div>
        )}

        <div className={styles['quota-group-card-layout']}>
          <div>
            <Card className={styles['behaviors-card']} elevation={1}>
              <h4>Purchase Behaviors</h4>
              <hr />
              <GroupMadlib
                activePrompt={activePrompt}
                getVisibleBehaviorCount={getVisibleBehaviorCount}
                group={group}
                isReadOnly={isReadOnly}
                onClearAnswer={onClearAnswer}
                onSelectAnswer={onUpdateAnswers}
                onSetActivePrompt={setActivePrompt}
                setVisibleBehaviorCount={setVisibleBehaviorCount}
                surveyType={surveyType}
                updateVisiblePrompts={updateVisiblePrompts}
              />
            </Card>
            {surveyType.advancedPrompts?.length > 0 && (
              <GroupAdvancedPrompts
                activePrompt={activePrompt}
                advancedPrompts={surveyType.advancedPrompts}
                group={group}
                isCompleted={isReadOnly}
                onClearAnswer={onClearAnswer}
                onUpdate={onUpdate}
                setActivePrompt={setActivePrompt}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles['quota-group-card-responses']}>
        <p>
          Responses
          <span>
            <strong>{group.quotaLimit}</strong>
            {!isReadOnly && (
              <MinusPlusButtons
                disableMinusButton={
                  !canDecrementResponses || group.quotaLimit - responseIncrement < responseMinimum
                }
                disablePlusButton={!canIncrementResponses}
                isNeutral
                onClickMinus={() => onUpdate({ quotaLimit: group.quotaLimit - responseIncrement })}
                onClickPlus={() => onUpdate({ quotaLimit: group.quotaLimit + responseIncrement })}
              />
            )}
          </span>
        </p>
        {(isGroupStatusUpdated || (isValidatingResponses && hasValidationCriteria)) && (
          <AvailablePanelists
            group={group}
            isLoading={isValidatingResponses}
            status={getGroupStatus()}
          />
        )}
        {CONCEPT_IMAGE_SURVEY_TYPES.includes(surveyType.id) && (
          <ConceptDetails group={group} isReadOnly={isReadOnly} onUpdate={onUpdate} />
        )}
      </div>
      {activePrompt && (
        <ModalPromptSearch
          isSurvey
          onClose={() => setActivePrompt(null)}
          onSubmit={onUpdateAnswers}
          prompt={activePromptForSearch}
          searchAnswers={activeHierarchySearchAnswers}
          searchPrompts={surveyType.hierarchy_search_prompts}
          selectedAnswers={activeSelectedAnswers.length === 0 ? null : activeSelectedAnswers} // null is a date range fix
        />
      )}
    </Card>
  );
};

QuotaGroupCard.propTypes = {
  canDecrementResponses: PropTypes.bool,
  canDelete: PropTypes.bool,
  canDuplicate: PropTypes.bool,
  canIncrementResponses: PropTypes.bool,
  getVisibleBehaviorCount: PropTypes.func,
  group: PropTypes.object.isRequired,
  isValidatingResponses: PropTypes.bool,
  isVideoSurvey: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  setVisibleBehaviorCount: PropTypes.func,
  survey: PropTypes.object.isRequired,
  surveyType: PropTypes.object.isRequired,
  updateVisiblePrompts: PropTypes.func,
};

export default QuotaGroupCard;
